'use client'

import {
  DeckSelectionResult,
  IowaGamblingTaskAnswer
} from '@/api/services/types/answerTypes'
import { IowaGamblingTask as IowaGamblingTaskType } from '@/api/services/types/questionTypes'
import { Card, CardContent } from '@/components/card'
import { Button } from '@/components/ui/common/Button'
import { useTestResponse } from '@/hooks/useTestResponse'
import { cn } from '@/utils/common'
import * as React from 'react'

interface DeckConfig {
  id: string
  label: string
  color: string
  isAdvantageous: boolean
  reward: number
  penalty: number
  penaltyProbabilityPercent: number
  cardCount: number
}

interface CardResult {
  deckId: string
  reward: number
  penalty: number
  netGain: number
}

interface TaskResult {
  finalBalance: number
  deckSelections: Record<string, number>
  advantageousSelections: number
  disadvantageousSelections: number
  learningIndex: number
  response: IowaGamblingTaskAnswer
}

interface IowaGamblingTaskProps {
  data: IowaGamblingTaskType
  initialBalance?: number
  onComplete?: (result: TaskResult) => void
  onStart?: () => void
  className?: string
  moduleId: string
  questionId: string
}

export function IowaGamblingTask({
  data,
  initialBalance = 2000,
  onComplete,
  onStart,
  className,
  moduleId,
  questionId
}: IowaGamblingTaskProps) {
  const { addQuestionResponse } = useTestResponse()
  const [stage, setStage] = React.useState<'intro' | 'task' | 'results'>('intro')
  const [balance, setBalance] = React.useState(initialBalance)
  const [decks, setDecks] = React.useState<DeckConfig[]>([])
  const [deckSelections, setDeckSelections] = React.useState<Record<string, number>>({})
  const [remainingCards, setRemainingCards] = React.useState<Record<string, number>>({})
  const [selectionHistory, setSelectionHistory] = React.useState<CardResult[]>([])
  const [lastSelection, setLastSelection] = React.useState<CardResult | null>(null)
  const [totalSelections, setTotalSelections] = React.useState(0)
  const [startTime] = React.useState(Date.now())
  const [isTaskCompleted, setIsTaskCompleted] = React.useState(false)

  React.useEffect(() => {
    initializeTask()
  }, [])

  const initializeTask = React.useCallback(() => {
    if (onStart) {
      onStart()
    }

    setIsTaskCompleted(false)

    const initializedDecks: DeckConfig[] = [
      {
        id: 'Z1',
        label: 'Z1',
        color: 'bg-red-500',
        isAdvantageous: false,
        reward: data.z1DeckReward,
        penalty: data.z1DeckPenalty,
        penaltyProbabilityPercent: data.z1DeckPenaltyProbabilityPercent,
        cardCount: data.z1DeckCardCount,
      },
      {
        id: 'Z2',
        label: 'Z2',
        color: 'bg-yellow-500',
        isAdvantageous: false,
        reward: data.z2DeckReward,
        penalty: data.z2DeckPenalty,
        penaltyProbabilityPercent: data.z2DeckPenaltyProbabilityPercent,
        cardCount: data.z2DeckCardCount,
      },
      {
        id: 'D1',
        label: 'D1',
        color: 'bg-green-500',
        isAdvantageous: true,
        reward: data.d1DeckReward,
        penalty: data.d1DeckPenalty,
        penaltyProbabilityPercent: data.d1DeckPenaltyProbabilityPercent,
        cardCount: data.d1DeckCardCount,
      },
      {
        id: 'D2',
        label: 'D2',
        color: 'bg-blue-500',
        isAdvantageous: true,
        reward: data.d2DeckReward,
        penalty: data.d2DeckPenalty,
        penaltyProbabilityPercent: data.d2DeckPenaltyProbabilityPercent,
        cardCount: data.d2DeckCardCount,
      }
    ]

    setDecks(initializedDecks)
    setBalance(initialBalance)
    setDeckSelections(Object.fromEntries(initializedDecks.map((deck) => [deck.id, 0])))
    setRemainingCards(
      Object.fromEntries(initializedDecks.map((deck) => [deck.id, deck.cardCount]))
    )
    setSelectionHistory([])
    setLastSelection(null)
    setTotalSelections(0)
    setStage('task')
  }, [initialBalance, data, onStart])

  const selectCard = (deckId: string) => {
    if (remainingCards[deckId] <= 0) return

    const selectedDeck = decks.find((deck) => deck.id === deckId)
    if (!selectedDeck) return

    
    // Losujemy czy karta powoduje karę na podstawie prawdopodobieństwa
    const randomValue = Math.random() * 100
    const incursPenalty = randomValue < selectedDeck.penaltyProbabilityPercent

    // Jeśli prawdopodobieństwo kary jest 100%, to zawsze jest tylko kara
    const reward = selectedDeck.penaltyProbabilityPercent === 100 ? 0 : selectedDeck.reward
    const penalty = incursPenalty ? -Math.abs(selectedDeck.penalty) : 0
    const netGain = reward + penalty

    const newBalance = balance + netGain
    const newDeckSelections = { ...deckSelections, [deckId]: deckSelections[deckId] + 1 }
    const newRemainingCards = { ...remainingCards, [deckId]: remainingCards[deckId] - 1 }
    const newTotalSelections = totalSelections + 1

    const result: CardResult = {
      deckId,
      reward,
      penalty,
      netGain
    }

    setBalance(newBalance)
    setDeckSelections(newDeckSelections)
    setRemainingCards(newRemainingCards)
    setLastSelection(result)
    setSelectionHistory([...selectionHistory, result])
    setTotalSelections(newTotalSelections)

    checkTaskCompletion(newTotalSelections, newRemainingCards)
  }

  const checkTaskCompletion = (selections: number, remaining: Record<string, number>) => {
    if (selections >= data.totalChoicesCount) {
      endTask()
      return
    }

    const allCardsUsed = Object.values(remaining).every((count) => count === 0)
    if (allCardsUsed) {
      endTask()
      return
    }
  }

  const endTask = () => {
    if (isTaskCompleted) return
    
    setIsTaskCompleted(true)
    const advantageousSelections = decks
      .filter((deck) => deck.isAdvantageous)
      .reduce((sum, deck) => sum + deckSelections[deck.id], 0)

    const disadvantageousSelections = decks
      .filter((deck) => !deck.isAdvantageous)
      .reduce((sum, deck) => sum + deckSelections[deck.id], 0)

    const learningIndex =
      totalSelections > 0
        ? (advantageousSelections - disadvantageousSelections) / totalSelections
        : 0

    const deckSelectionsResponse: DeckSelectionResult[] = decks.map((deck) => ({
      deckNumber: deck.id,
      amount: deckSelections[deck.id]
    }))

    const responseTimeMillis = Date.now() - startTime

    const response: IowaGamblingTaskAnswer = {
      deckSelections: deckSelectionsResponse,
      responseTimeMillis: responseTimeMillis
    }

    const result: TaskResult = {
      finalBalance: balance,
      deckSelections,
      advantageousSelections,
      disadvantageousSelections,
      learningIndex,
      response
    }

    // Zapisz odpowiedź w kontekście testu
    addQuestionResponse(moduleId, {
      questionId,
      questionType: 'IOWA_GAMBLING_TASK',
      iowaGamblingTaskAnswer: {
        ...response,
        responseTimeMillis
      }
    })

    if (onComplete) {
      onComplete(result)
    }

    // setStage('results')
  }

  const renderIntro = () => (
    <div className="flex flex-col items-center gap-6">
      <h1 className="text-center text-2xl font-bold">Iowa Gambling Task</h1>

      <p className="max-w-lg text-center">
        W tym zadaniu będziesz wybierać karty z czterech talii, aby zdobyć jak najwięcej
        pieniędzy. Każda karta przynosi nagrodę, ale niektóre karty wiążą się również z
        karą.
      </p>

      <p className="max-w-lg text-center">
        Niektóre talie są bardziej korzystne długoterminowo niż inne. Twoim zadaniem jest
        odkryć, które talie przynoszą największe zyski w dłuższej perspektywie.
      </p>

      <div className="w-full rounded-md bg-[#2bcaca]/10 p-4">
        <ul className="list-disc space-y-2 pl-6">
          <li>Zaczynasz z kwotą {initialBalance} zł</li>
          <li>Każda karta przynosi nagrodę, ale niektóre wiążą się również z karą</li>
          <li>Każda talia ma określone prawdopodobieństwo kary (wyświetlane pod taliami)</li>
          <li>Możesz wybierać karty z dowolnej talii</li>
          <li>
            Zadanie kończy się po wybraniu wszystkich kart lub po osiągnięciu maksymalnej
            liczby wyborów
          </li>
        </ul>
      </div>

      <Button
        className="bg-indigo-600 text-white hover:bg-indigo-700"
        onClick={initializeTask}
      >
        Rozpocznij zadanie
      </Button>
    </div>
  )

  const renderTask = () => (
    <div className="flex flex-col items-center gap-6">
      <h2 className="text-center text-xl font-bold">Wybierz kartę z jednej z talii</h2>

      <div className="text-center">
        <p className="text-sm text-gray-600">Twoje pieniądze:</p>
        <p className="text-4xl font-bold text-[#2bcaca]">{balance} zł</p>
      </div>

      {lastSelection && (
        <div className="w-full max-w-md rounded-md bg-gray-50 p-4">
          <p className="text-center font-medium">
            Ostatni wybór (Talia {lastSelection.deckId}):
          </p>
          {lastSelection.reward > 0 && (
            <p className="text-center">
              Nagroda:{' '}
              <span className="text-green-600">
                +{lastSelection.reward} zł
              </span>
            </p>
          )}
          {lastSelection.penalty < 0 && (
            <p className="text-center">
              Kara: <span className="text-red-600 font-medium">{lastSelection.penalty} zł</span>
              {/* <span className="text-sm text-gray-500 ml-1">
                (Prawdopodobieństwo: {decks.find(d => d.id === lastSelection.deckId)?.penaltyProbabilityPercent}%)
              </span> */}
            </p>
          )}
          <p className="text-center">
            Zysk netto:{' '}
            <span
              className={lastSelection.netGain >= 0 ? 'text-green-600' : 'text-red-600 font-medium'}
            >
              {lastSelection.netGain > 0
                ? `+${lastSelection.netGain}`
                : lastSelection.netGain}{' '}
              zł
            </span>
          </p>
        </div>
      )}

      <div className="grid w-full max-w-2xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {decks.map((deck) => (
          <div className="flex flex-col items-center w-full" key={deck.id}>
            <button
              className={cn(
                'flex w-full aspect-[3/4] items-center justify-center rounded-md border-2 border-gray-200',
                'transition-all hover:border-gray-400 focus:ring-2 focus:ring-indigo-500 focus:outline-none',
                'max-w-[280px]',
                remainingCards[deck.id] <= 0 && 'cursor-not-allowed opacity-50'
              )}
              disabled={remainingCards[deck.id] <= 0}
              onClick={() => selectCard(deck.id)}
            >
              <div
                className={cn(
                  'flex w-[85%] aspect-[3/4] items-center justify-center rounded',
                  deck.color,
                  'text-3xl sm:text-4xl font-bold text-white'
                )}
              >
                {deck.label}
              </div>
            </button>
            <p className="mt-2 text-sm text-gray-600">
              Pozostało: {remainingCards[deck.id]} / {deck.cardCount}
            </p>
            <p className="text-sm text-gray-600">Wybrano: {deckSelections[deck.id]}</p>
            {/* {deck.penaltyProbabilityPercent === 100 ? (
              <p className="text-sm text-red-600 font-medium">
                Kara: {deck.penalty} zł (100% szans)
              </p>
            ) : (
              <>
                <p className="text-sm text-green-600">
                  Nagroda: +{deck.reward} zł
                </p>
                <p className="text-sm text-red-600 font-medium">
                  Kara: {deck.penalty} zł ({deck.penaltyProbabilityPercent}% szans)
                </p>
              </>
            )} */}
          </div>
        ))}
      </div>

      <div className="text-sm text-gray-600">
        Pozostało wyborów: {data.totalChoicesCount - totalSelections}
      </div>
    </div>
  )

  return (
    <Card className={cn('mx-auto w-full max-w-3xl', className)}>
      <CardContent className="p-6">
        {stage === 'intro' && renderIntro()}
        {stage === 'task' && renderTask()}
        {/* {stage === 'results' && renderResults()} */}
      </CardContent>
    </Card>
  )
}
